/**
 * Copyright 2026, IntraLinks, Inc. All rights reserved.
 */

import React, {
  ChangeEvent, FormEvent, useEffect, useRef, useState
} from 'react';
import {
  IlButton, IlInlineLoading, IlTextInput, IlInlineNotification
} from 'il-framework-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { RootState } from '../../../../../app/store';
import { DEFAULT_EMAIL_DIGITS, I18nNamespaces, LOA_RESEND_SECONDS } from '../../../../../app/shared/utils/constants.util';
import { AuthErrorKey, SecuritySetupType } from '../../../../../domain/models/impl/SecuritySetup';
import { maskEmailFixed, getLoaValidationMessageKey } from '../../../../../app/shared/utils/helpers.util';
import {
  setError,
  setValidationError,
  profileEnrollEmailFactor,
  profileChallengeFactor,
  profileActivateFactor,
  resetProfileEnrollState
} from '../../profileEnrollSlice';

import styles from './ProfileEnrollEmail.scss';

interface ProfileEnrollEmailProps {
  loaLevel: number;
  onEnrolled: () => void;
  onCancel: () => void;
}

const ProfileEnrollEmail: React.FC<ProfileEnrollEmailProps> = ({
  loaLevel,
  onEnrolled,
  onCancel
}) => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();

  const actionLoading = useSelector((s: RootState) => s.profileEnroll.actionLoading);
  const validationError = useSelector((s: RootState) => s.profileEnroll.validationError);
  const emailErrorKey = useSelector((s: RootState) => s.profileEnroll.error);
  const selectedFactor = useSelector((s: RootState) => s.profileEnroll.selectedFactor);

  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const initOnceRef = useRef(false);

  const factorId = selectedFactor?.factorId ?? '';
  const digits = selectedFactor?.digits ?? DEFAULT_EMAIL_DIGITS;
  const typedDigits = code.replace(/\D/g, '').length;
  const canSubmit = !!factorId && digits > 0 && !actionLoading && !validationError && typedDigits === digits;
  const canResend = !!factorId && resendIn === 0 && !actionLoading;
  const profileEmail = useSelector((s: RootState) => s.profile.profileDetails?.externalId);
  const emailText = maskEmailFixed(profileEmail) || t('loaEnrichment.factors.email.fallbackEmail');
  const isEmailFactor = selectedFactor?.type === SecuritySetupType.EMAIL;

  const isInitLoading = actionLoading && (!isEmailFactor || !factorId);

  const inlineTitleMap: Record<string, string> = {
    [AuthErrorKey.DefaultEmailErrorMessage]: t('loaEnrichment.factors.email.inline.startFailed'),
    [AuthErrorKey.DefaultChallengeError]: t('loaEnrichment.common.errors.challengeFailedInline')
  };

  const inlineTitle = inlineTitleMap[emailErrorKey]
    ?? t('loaEnrichment.factors.email.errors.defaultEmailInlineNotification');

  useEffect(() => () => {
    dispatch(resetProfileEnrollState());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedFactor && !initOnceRef.current) {
      initOnceRef.current = true;

      (async () => {
        const newFactorId = await dispatch(profileEnrollEmailFactor(t, { loaLevel }) as any);
        if (newFactorId) {
          setResendIn(LOA_RESEND_SECONDS);
        } else {
          initOnceRef.current = false;
        }
      })();
    }
  }, [dispatch, selectedFactor, loaLevel, t]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  const clearErrors = () => {
    dispatch(setError(''));
    dispatch(setValidationError(''));
  };

  const updateCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors();
    const input = e.currentTarget.value.replace(/\D/g, '');
    setCode(input.slice(0, digits));
  };

  const sendCode = async () => {
    if (!factorId) return;
    setResendIn(LOA_RESEND_SECONDS);
    await dispatch(profileChallengeFactor(t, {
      factorId,
      toastKey: 'loaEnrichment.common.errors.sendFailedToast',
      errorKey: AuthErrorKey.DefaultChallengeError
    }) as any);
  };

  const submit = async (ev: FormEvent | ChangeEvent) => {
    ev.preventDefault();
    if (!factorId) return;

    const success = await dispatch(profileActivateFactor(t, {
      factorId,
      code: code.replace(/\s/g, ''),
      action: 'activate'
    }) as any);

    if (success) {
      onEnrolled();
    }
  };

  if (isInitLoading) {
    return (
      <div className={styles.initLoadingContainer}>
        <IlInlineLoading description={t('loaEnrichment.common.status.pleaseWait')} success={false} onSuccess={noop} />
      </div>
    );
  }

  return (
    <>
      <h1 className={styles.formTitle}>{t('loaEnrichment.factors.email.title')}</h1>
      <p className={styles.helpText}>{t('loaEnrichment.factors.email.verifyHelpText', { email: emailText })}</p>

      {emailErrorKey && (
        <div className={styles.inlineNotification}>
          <IlInlineNotification
            title={inlineTitle}
            notificationType='inline'
            kind='error'
            hideCloseButton
          />
        </div>
      )}

      <form onSubmit={submit}>
        <IlTextInput
          id='profile-email-activation-code'
          labelText={t('loaEnrichment.common.labels.verificationCode')}
          placeholder={t('loaEnrichment.common.placeholders.code', { digits })}
          type='text'
          maxLength={digits}
          invalid={!!validationError}
          invalidText={t(getLoaValidationMessageKey(validationError) ?? 'loaEnrichment.common.errors.default')}
          value={code}
          onChange={updateCode}
          autoFocus
          autoComplete='off'
        />

        <div className={styles.activationContainer}>
          <div className={styles.resendContainer}>
            <p className={styles.resendText}>{t('loaEnrichment.common.resend.emailPrompt')}</p>
            <IlButton
              className={styles.resendBtn}
              kind='ghost'
              type='button'
              disabled={!canResend}
              onClick={sendCode}
            >
              {resendIn > 0 ? t('loaEnrichment.common.resend.resendIn', { seconds: resendIn })
                : t('loaEnrichment.common.buttons.resendCode')}
            </IlButton>
          </div>

          <div className={styles.actionBtnsContainer}>
            <IlButton kind='secondary' type='button' onClick={onCancel}>
              {t('loaEnrichment.common.buttons.cancel', { defaultValue: 'Cancel' })}
            </IlButton>
            {' '}
            <IlButton kind='primary' type='submit' disabled={!canSubmit}>
              {t('loaEnrichment.common.buttons.activate')}
            </IlButton>
          </div>
        </div>
      </form>

      {actionLoading && factorId && (
        <div className={styles.loadingContainer}>
          <IlInlineLoading description={t('loaEnrichment.common.status.pleaseWait')} success={false} onSuccess={noop} />
        </div>
      )}
    </>
  );
};

export default ProfileEnrollEmail;
